import { Box, Dialog } from '@mui/material'
import { useEffect, useState } from 'react'
import {
  FONT_SANS,
  PillButton,
  RADII,
  SchedulerThemeScope,
  TOKENS,
} from './SchedulerDesign'

/**
 * Ask for a schedule name — shared by Rename and Clone on the schedule list.
 * `initialName` pre-fills the box (the current name, or "… (copy)").
 */
export const ScheduleNameDialog = ({
  open,
  title,
  confirmLabel,
  initialName,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean
  title: string
  confirmLabel: string
  initialName: string
  busy?: boolean
  onClose: () => void
  onConfirm: (name: string) => void
}) => {
  const [name, setName] = useState(initialName)
  useEffect(() => {
    if (open) setName(initialName)
  }, [open, initialName])
  const trimmed = name.trim()
  const submit = () => {
    if (trimmed && !busy) onConfirm(trimmed)
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: RADII.md, border: `1px solid ${TOKENS.line200}` },
      }}
    >
      <SchedulerThemeScope>
        <Box sx={{ px: 2.5, pt: 2, pb: 1, fontSize: 16, fontWeight: 700 }}>
          {title}
        </Box>
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Box
            component="label"
            htmlFor="schedule-name"
            sx={{
              display: 'block',
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.ink400,
              mb: '4px',
            }}
          >
            Schedule name
          </Box>
          <Box
            component="input"
            id="schedule-name"
            autoFocus
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') submit()
            }}
            sx={{
              width: '100%',
              height: 32,
              px: '8px',
              boxSizing: 'border-box',
              borderRadius: '2px',
              border: `1px solid ${TOKENS.line300}`,
              fontFamily: FONT_SANS,
              fontSize: 13,
              color: TOKENS.ink900,
              '&:focus': { outline: 'none', borderColor: TOKENS.brandBlue },
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${TOKENS.line200}`,
          }}
        >
          <PillButton kind="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </PillButton>
          <PillButton
            kind="primary"
            onClick={submit}
            disabled={!trimmed || busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </PillButton>
        </Box>
      </SchedulerThemeScope>
    </Dialog>
  )
}
